import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import { useEffect } from "react";

import styled from "styled-components/macro";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks";

/* USAGE EXAMPLE

  <FnrListOption
    label="Label"
    sublabel="Sublabel"
    disabled={false}
    imageUrl={imageUrl}
    defaultImageUrl={defaultImageUrl}
    onClick={handleClick}
    indent={indent}
  />

*/

FnrListOption.propTypes = {
  label: PropTypes.string,
  sublabel: PropTypes.string,
  isSelected: PropTypes.bool,
  isLoading: PropTypes.bool,
  variants: PropTypes.object,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.object,
  iconColor: PropTypes.string,
  imageUrl: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  textColor: PropTypes.string,
  indent: PropTypes.number, // 0, 1, 2 => Used for displaying group heirarchy
  applyFocus: PropTypes.bool,
};

export default function FnrListOption({
  defaultImageUrl,
  disabled,
  indent,
  label,
  sublabel,
  onClick = () => {},
  icon,
  iconColor = "#278dc1",
  imageUrl,
  isSelected,
  isLoading,
  variants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  },
  textColor = "#222",
  applyFocus,
}) {
  const buttonRef = useRef();

  useEffect(() => {
    if (isSelected) buttonRef.current.focus();
  }, [isSelected]);

  useEffect(() => {
    // this is a bit hacky... but is used to apply focus to the first item in a list, which allows
    // tab naviation when using the FnrSelectList via a portal. If an item is selected, it's
    // focus call will override this one.
    if (applyFocus) buttonRef.current.focus();
    // eslint-disable-next-line
  }, []);

  const getPhotoSrc = () => {
    if (imageUrl) return imageUrl;
    else if (defaultImageUrl) return defaultImageUrl;
    else return "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg";
  };

  const renderImage = () => {
    if (icon) {
      return (
        <IconWrapper color={iconColor}>
          <FontAwesomeIcon icon={icon} />
        </IconWrapper>
      );
    } else if (imageUrl !== undefined) {
      // if the imageUrl prop is not included at all, it will be undefined.
      // so either a string or null means they are attempting to use an image
      return <Img src={getPhotoSrc()} />;
    } else {
      return null;
    }
  };

  const renderEndOfRowIcon = () => {
    if (isLoading) {
      return (
        <EndofRowWrapper>
          <FnrLoadingBricks show={true} size={24} />
        </EndofRowWrapper>
      );
    } else if (isSelected) {
      return (
        <EndofRowWrapper color={textColor}>
          <FontAwesomeIcon icon={faCheck} size="lg" />
        </EndofRowWrapper>
      );
    } else {
      return null;
    }
  };

  return (
    <AnimatePresence>
      <Wrapper variants={variants} indent={indent}>
        <Button
          type="button"
          onClick={onClick}
          disabled={disabled}
          ref={buttonRef}
        >
          {renderImage()}

          <NameWrapper>
            <Name color={textColor}>{label}</Name>
            {sublabel ? <Description>{sublabel}</Description> : null}
          </NameWrapper>

          {renderEndOfRowIcon()}
        </Button>
      </Wrapper>
    </AnimatePresence>
  );
}

const Wrapper = styled(motion.li)`
  margin-left: ${({ indent }) => (indent === 2 ? "10px" : null)};
  margin-left: ${({ indent }) => (indent === 3 ? "20px" : null)};
`;

const Button = styled.button`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 8px;
  min-height: 40px;
  border-radius: 5px;
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};
  transition: background 200ms;

  &:hover {
    background: ${(props) =>
      props.disabled ? "transparent" : "rgba(69, 148, 253, 0.1)"};
  }

  &:focus {
    background: ${(props) =>
      props.disabled ? "transparent" : "rgba(69, 148, 253, 0.1)"};
  }
`;

const Img = styled.img`
  height: 24px;
  width: 24px;
  border-radius: 100%;
  margin-right: 8px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 24px;
  margin-right: 8px;
  color: ${(props) => props.color};
`;

const NameWrapper = styled.div`
  text-align: left;
`;

const Name = styled.div`
  font-weight: 300;
  color: ${(props) => props.color};
  font-size: 14px;
`;

const Description = styled.div`
  font-size: 11px;
  color: #222;
  opacity: 0.6;
  margin-top: 1px;
`;

const EndofRowWrapper = styled.div`
  margin-left: auto;
  color: ${(props) => props.color};
`;
